import { SelectOption } from '@components/v2/form/Select/select.types';
import LangEnum from '../../../../../../../../../lang/LangEnum';
import { useUpdateCatalog } from '../../../../../../../../../services/modules/catalogs/catalogs.mutations';
import { formatDateToWithoutTimezone, toDateOrNull } from '../../../../../../../../../utils/dates';
import { isStartDateBeforeEndDate } from '../../../../../../../../../utils/schemaUtils/validateIsStartDateBeforeEndDate.utils';
import { useTranslation } from '../../../../../../../../translation/TranslationContext';
import { UpdateCatalogParams } from '../../../../../../../repositories/types/catalogs.repository.types';
import { isYoutubeVideoUrl } from '../../../../../catalogLiveShop.utils';
import { formatCatalogDateTime } from '../../../catalogDetailsPanel.utils';
import { CatalogGeneralInfoSectionProps } from '../CatalogGeneralInfoSection';

const PUBLIC_SHARING_ENABLED_VALUE = 'true';
const ACTIVE_STATUS_VALUE = 'true';

const useCatalogGeneralInfoSection = ({
  id,
  active,
  isDefault,
  isDynamic,
  isReadOnly,
  enablePublicSharing,
  startDate,
  endDate,
}: CatalogGeneralInfoSectionProps) => {
  const { translate } = useTranslation();
  const { mutate } = useUpdateCatalog({ catalogId: id });

  const typeLabel = isDynamic
    ? LangEnum.CATALOGS_CATALOG_TYPE_DYNAMIC
    : LangEnum.CATALOGS_CATALOG_TYPE_STATIC;

  const readOnlyFields = {
    type: translate(typeLabel),
  };

  const statusOptions: SelectOption[] = [
    { value: ACTIVE_STATUS_VALUE, label: translate(LangEnum.GLOBAL_ACTIVE) },
    { value: 'false', label: translate(LangEnum.GLOBAL_INACTIVE) },
  ];

  const publicSharingOptions: SelectOption[] = [
    { value: PUBLIC_SHARING_ENABLED_VALUE, label: translate(LangEnum.GLOBAL_ALLOWED) },
    { value: 'false', label: translate(LangEnum.GLOBAL_BLOCKED) },
  ];

  const dateFields = {
    startDate: toDateOrNull(startDate),
    endDate: toDateOrNull(endDate),
  };

  const dateLabels = {
    startDate: formatCatalogDateTime(startDate),
    endDate: formatCatalogDateTime(endDate),
  };

  const isFieldDisabled = isReadOnly || isDefault;

  const validateRequired = (value: string) =>
    value.trim().length === 0 ? translate(LangEnum.GLOBAL_REQUIRED) : undefined;

  const validateLiveLink = (value: string) => {
    const liveLink = value.trim();

    if (!liveLink || isYoutubeVideoUrl(liveLink)) {
      return undefined;
    }

    return translate(LangEnum.CATALOG_LIVE_SHOP_INVALID_LINK);
  };

  const validateStartDate = (value: Date | null) =>
    isStartDateBeforeEndDate({ startDate: value, endDate: dateFields.endDate })
      ? undefined
      : translate(LangEnum.GLOBAL_START_DATE_ERROR);

  const validateEndDate = (value: Date | null) =>
    isStartDateBeforeEndDate({ startDate: dateFields.startDate, endDate: value })
      ? undefined
      : translate(LangEnum.GLOBAL_END_DATE_ERROR);

  const submit = (payload: UpdateCatalogParams) => mutate(payload);

  const submitPublicSharing = (value: string | number) =>
    submit({ enablePublicSharing: value === PUBLIC_SHARING_ENABLED_VALUE });

  const submitStatus = (value: string | number) =>
    submit({ active: value === ACTIVE_STATUS_VALUE });

  const submitStartDate = (value: Date | null) =>
    submit({ startDate: formatDateToWithoutTimezone(value) });

  const submitEndDate = (value: Date | null) =>
    submit({ endDate: formatDateToWithoutTimezone(value) });

  return {
    readOnlyFields,
    publicSharingValue: String(enablePublicSharing),
    publicSharingOptions,
    statusValue: String(active),
    statusOptions,
    dateFields,
    dateLabels,
    isFieldDisabled,
    isStatusDisabled: isReadOnly,
    validateRequired,
    validateLiveLink,
    validateStartDate,
    validateEndDate,
    submit,
    submitPublicSharing,
    submitStatus,
    submitStartDate,
    submitEndDate,
  };
};

export default useCatalogGeneralInfoSection;
